// src/pages/VehicleLocationsLineChart.jsx
import React from "react";
import { ResponsiveLine } from "@nivo/line";

const VehicleLocationsLineChart = ({
  dayData = [],
  nightData = [],
  groupLevel,
  onItemSelect,
  colors,
}) => {
  // Combinar datos (día + noche)
  const dict = {};
  dayData.forEach((item) => {
    const key = item.level || "SIN_DIA";
    if (!dict[key]) dict[key] = { day: 0, night: 0 };
    dict[key].day += Number(item.total_vehiculos || 0);
  });
  nightData.forEach((item) => {
    const key = item.level || "SIN_NOCHE";
    if (!dict[key]) dict[key] = { day: 0, night: 0 };
    dict[key].night += Number(item.total_vehiculos || 0);
  });

  // Convertir a array y ordenar
  let combinedArray = Object.keys(dict)
    .map((key) => ({
      name: key,
      day: dict[key].day,
      night: dict[key].night,
      total: dict[key].day + dict[key].night,
    }))
    .sort((a, b) => b.total - a.total);

  // Top 10 + otros
  const TOP_N = 10;
  const topItems = combinedArray.slice(0, TOP_N);
  const others = combinedArray.slice(TOP_N);
  if (others.length > 0) {
    const sumOthers = others.reduce(
      (acc, curr) => {
        acc.day += curr.day;
        acc.night += curr.night;
        acc.total += curr.total;
        return acc;
      },
      { name: "OTRAS", day: 0, night: 0, total: 0 }
    );
    topItems.push(sumOthers);
  }

  // Crear series con colores fijos
  const series = [
    {
      id: "Diurno",
      data: topItems.map((item) => ({
        x: item.name,
        y: item.day,
      })),
    },
    {
      id: "Nocturno",
      data: topItems.map((item) => ({
        x: item.name,
        y: item.night,
      })),
    },
  ];

  // Definir colores fijos para las líneas
  const lineColors = {
    Diurno: colors.primary,
    Nocturno: colors.secondary,
  };

  const theme = {
    background: colors.cardBg,
    axis: {
      domain: {
        line: {
          stroke: colors.border,
          strokeWidth: 1,
        },
      },
      ticks: {
        line: {
          stroke: colors.border,
          strokeWidth: 1,
        },
        text: {
          fill: colors.text,
          fontSize: 12,
        },
      },
      legend: {
        text: {
          fill: colors.text,
          fontSize: 13,
          fontWeight: 500,
        },
      },
    },
    grid: {
      line: {
        stroke: colors.border,
        strokeWidth: 1,
        strokeDasharray: "4 4",
      },
    },
    crosshair: {
      line: {
        stroke: colors.primary,
        strokeWidth: 1,
        strokeOpacity: 0.35,
      },
    },
    legends: {
      text: {
        fill: colors.text,
        fontSize: 12,
      },
    },
    tooltip: {
      container: {
        background: colors.cardBg,
        color: colors.text,
        fontSize: 12,
        borderRadius: 8,
        boxShadow: colors.shadow,
        border: `1px solid ${colors.border}`,
      },
    },
  };

  return (
    <div style={{ height: "400px", background: colors.cardBg }}>
      <ResponsiveLine
        data={series}
        margin={{ top: 50, right: 110, bottom: 80, left: 60 }}
        xScale={{ type: "point" }}
        yScale={{
          type: "linear",
          min: 0,
          max: "auto",
          stacked: false,
          reverse: false,
        }}
        theme={theme}
        curve="monotoneX"
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 45,
          legend: groupLevel.toUpperCase(),
          legendOffset: 60,
          legendPosition: "middle",
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: "Vehículos",
          legendOffset: -40,
          legendPosition: "middle",
        }}
        enableGridX={false}
        enableGridY={true}
        colors={({ id }) => lineColors[id]}
        pointSize={8}
        pointColor={colors.cardBg}
        pointBorderWidth={2}
        pointBorderColor={{ from: "serieColor" }}
        pointLabelYOffset={-12}
        enableArea={true}
        areaOpacity={0.1}
        useMesh={true}
        enableSlices="x"
        crosshairType="cross"
        legends={[
          {
            anchor: "bottom-right",
            direction: "column",
            justify: false,
            translateX: 100,
            translateY: 0,
            itemsSpacing: 0,
            itemDirection: "left-to-right",
            itemWidth: 80,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: "circle",
            symbolBorderColor: colors.border,
            effects: [
              {
                on: "hover",
                style: {
                  itemBackground: colors.hover,
                  itemOpacity: 1,
                  itemTextColor: colors.primary,
                },
              },
            ],
          },
        ]}
        motionConfig={{
          mass: 1,
          tension: 170,
          friction: 26,
          clamp: false,
          precision: 0.01,
          velocity: 0,
        }}
        role="application"
        ariaLabel="Gráfico de líneas de vehículos por ubicación"
        onClick={(point) => {
          if (onItemSelect) onItemSelect(point.data.x);
        }}
      />
    </div>
  );
};

export default VehicleLocationsLineChart;
